import _ from 'lodash'
import { compose, compact } from 'lodash/fp'
import Mustache from 'mustache'

import VALUES from 'core/data-model/constants/values'
import {
  isLifeProtectProduct,
  isWholeLife12PLProduct,
  isLifeEnsureProduct,
  isLifeLegacyProduct,
  isS7Product,
} from 'core/service/basic-plan/benefit'
import {
  calculateCashDrop,
  findCashDropRate,
  queryCashDropRate,
  findNonGuaranteeCashDropRate,
} from 'core/service/basic-plan/cash-drop'
import {
  findDividendRate,
  queryDividendRate,
  calculateCurrentIndex,
  calculateDividendGlobalIndexProduct,
  calculateDividendSurrender,
} from 'core/service/basic-plan/dividend'
import {
  findRPURate,
  findPENRate,
  findETIRate,
  queryRPURate,
  queryPENRate,
  queryETIRate,
  calculateRPU,
  calculateETI,
} from 'core/service/basic-plan/RPU-ETI-PEN'
import { getSumAssuredMRTA, findSaRate } from 'core/service/basic-plan/sa'
import { queryCashValueRate, getSurrenderCashMRTA } from 'core/service/basic-plan/surrender-cash'
import { asYears } from 'core/service/insured/age'
import { d } from 'core/service/lib/decimal'
import { round, formatNumber, roundDown } from 'core/service/lib/number-format'
import { getToggles } from 'quick-quote/feature-toggles'
import { isGLTSPGroup } from 'quick-quote/product-mrta/coverage-plan/selectors'

import MESSAGES from '../../../data-model/constants/bi-messages'
import {
  getPresentRate,
  getPensionTotal,
  getPensionTotalBumnanReady,
  getPensionTotalBumnanSmart,
  calculatePresentValue,
  calculatePensionAmount,
} from '../products/retirement'

import {
  getInitialAge,
  getAgeRangeWithinCoveragePeriod,
  calculateAnnualBasicPremium,
  calculateGuarenteeDividend,
  calculateInterestNonGuaranteeDividendDeposit,
  getSurrenderCash,
  calculateDeathBenefit,
  calculateMaturity,
  sumPolicyValuesByColumn,
  calculateIRR,
  calculateXIRR,
} from './utils'

export const _accumulateBasicPremium = (age, yearEnd, paymentPeriod, basicPremium) => {
  const lastPaymentYear = (() => {
    switch (paymentPeriod.type) {
      case 'limited_by_duration':
        return paymentPeriod.value

      case 'limited_to_age':
        return paymentPeriod.value - asYears(age)

      case 'by_package':
        throw new Error('Basic plan payment period should never determined by package')

      default:
        throw new Error('This should never be thrown due to exhaustive checking')
    }
  })()

  if (yearEnd <= lastPaymentYear) {
    return basicPremium.times(yearEnd).toNumber()
  }

  return basicPremium.times(lastPaymentYear).toNumber()
}

export const getMaxValue = (policyValues) => {
  let maxValue = -1
  for (let cmpValue of policyValues) {
    maxValue = maxValue > cmpValue ? maxValue : cmpValue
  }
  return maxValue
}

// Sorry to make this because keep in time to market.
// they want to reduce age -1 to display on policy value table only LifeSave+
// ** when they want to apply to reduce age to all product. should remove this function and
// focus on calculateTotalRiderPremiumAtYearEnd someone put login to reduce when push to calculated
// and remove + 1 at initialAge you can search in these files.
export const _reduceAgeToRender = (props) => {
  const reduceAgeProps = { ...props }
  _.set(reduceAgeProps, 'age.value', _.get(reduceAgeProps, 'age.value', 0) - 1)
  return reduceAgeProps
}

const decimalPrecision = (basicPlanCode) => {
  if (basicPlanCode === VALUES.ISMART_80_6) {
    return 2
  }
  return 0
}

export const _generatePolicyValuesForSaving = (
  policyValueParams,
  basicPlan,
  cashValueRates,
  riderPremiumRatesPayload,
  occupationFactor,
  cashDropRates
) => {
  const {
    age,
    gender,
    sumAssured,
    basicPremium,
    modelFactorPeriod,
    validSelectedRiders,
    riderBenefitTables,
  } = policyValueParams

  const productCashDropRate = findCashDropRate(basicPlan.code, cashDropRates)
  const benefits = _.get(riderBenefitTables, ['IGROW', 'riderBenefitData', 'benefits'], [])
  const accidentDeathBenefit = _.get(benefits[15], 'value', '').replace(/,/g, '')

  let cashValue
  let cashValueRate
  let cashDropValue
  let cashDropRate
  let deathBenefit = 0
  const yearEndPolicies = []
  let ageValue = age.unit !== 'year' ? 0 : age.value
  let isNotCalculated = false
  let isNotDisplay = false
  let riderPremium = d(0)
  let riderExtraPremium = d(0)
  let minPaymentPeriod = 7
  let basicPremiumAmt
  let riderPremiumAmt
  let maxDeathBenefitAmt = 1

  for (let rider of validSelectedRiders) {
    riderPremium = d(riderPremium.plus(d(_.get(rider, 'premium'))))
    if (_.get(rider, 'yearsOfPayment') !== basicPlan.paymentPeriod.value) {
      riderExtraPremium = d(riderExtraPremium.plus(d(_.get(rider, 'premium'))))
      minPaymentPeriod =
        _.get(rider, 'yearsOfPayment') < minPaymentPeriod ? _.get(rider, 'yearsOfPayment') : minPaymentPeriod
    }
  }

  for (let i = 1; i < basicPlan.coveragePeriod.value + 1; i++) {
    ageValue = ageValue + 1
    cashValueRate = queryCashValueRate(basicPlan.code, gender, age, i, cashValueRates)

    cashDropRate = _.get(productCashDropRate, ['cashDropRates', 'F', '0', `${ageValue}`], 0)
    cashDropValue =
      cashDropRate === 0
        ? '-'
        : d(sumAssured)
            .times(d(cashDropRate))
            .dividedBy(100)
    cashValue = d(sumAssured)
      .times(d(cashValueRate))
      .dividedBy(1000)
    if (i < 8) deathBenefit = _.get(benefits[i], 'value', '').replace(/,/g, '')
    isNotDisplay = ageValue > basicPlan.coveragePeriod.value
    basicPremiumAmt = basicPremium * modelFactorPeriod
    riderPremiumAmt =
      i > minPaymentPeriod ? (riderPremium - riderExtraPremium) * modelFactorPeriod : riderPremium * modelFactorPeriod
    isNotCalculated = i > basicPlan.paymentPeriod.value

    if (isNotDisplay) break
    maxDeathBenefitAmt = getMaxValue([
      isNotCalculated ? -1 : basicPremiumAmt * i,
      parseFloat(deathBenefit),
      cashValue.toNumber(),
      maxDeathBenefitAmt,
    ])
    let policyValue = {
      age: { unit: 'year', value: isNotDisplay ? -1 : ageValue },
      yearEndPolicy: i,
      cumulativeBasicPremium: isNotCalculated ? -1 : basicPremiumAmt * i,
      annualBasicPremium: isNotCalculated ? -1 : basicPremiumAmt,
      annualRiderPremium: isNotCalculated ? -1 : round(d(riderPremiumAmt).toNumber(), 2),
      annualTotalPremium: isNotCalculated
        ? -1
        : round(
            d(basicPremiumAmt)
              .plus(d(riderPremiumAmt))
              .toNumber(),
            2
          ),
      cashDropValue: isNotDisplay
        ? ''
        : cashDropValue !== '-'
        ? formatNumber(cashDropValue.toNumber(), 2, true)
        : cashDropValue,
      deathBenefitDisplay: isNotDisplay ? '' : formatNumber(maxDeathBenefitAmt, 2, true),
      accidentDeathBenefit: isNotDisplay ? '' : formatNumber(accidentDeathBenefit, 2, true),
      cashvalue: isNotDisplay ? -1 : round(cashValue.toNumber(), 2),
      surrenderCash: 0,
      deathBenefit: 0,
      basicSumAssured: 0,
    }
    yearEndPolicies.push(policyValue)
  }
  return {
    yearEndPolicyValues: yearEndPolicies,
    subtotalAnnualBasicPremium: 0,
    subtotalAnnualRiderPremium: 0,
    grandTotalPremium: 0,
  }
}

const _plancodeForReduceAgeToRender = [
  'WL88J',
  'WL88S',
  'W85P10J',
  'W85P10S',
  'R05',
  'R01',
  '25P15S',
  '25P15',
  '20P10S',
  '20P10',
  'PLB05',
  'PLB10',
  'PLB12',
  'PLB15',
  'ICARE',
  'WLNP60L',
  'WLNP99L',
  'WLNP85',
  'WLCI05',
  'WLCI10',
  'WLCI15',
  'WLCI20',
  'WLCI20W',
  'FWLNP85',
  'CIWLM39',
  'CIWLM50',
  'CIWLM60',
  'CIWLS39',
  'CIWLS50',
  'CIWLS65',
  'IHEALTHYULTRA-PWLNP85',
  'IHEALTHYULTRA-PWLNP60',
  'W99F99H',
  'W99F99M',
  'W99FU06',
  'W99FU12',
  'W99U18W',
  'W99H18W',
  'W99FU18',
  'W99FH18',
  'W99FU99',
  'W99FH99',
  'E20F10',
  'E25F15',
  'E25F25',
  'S14G5T1',
  'W80F06',
  'H99F06A',
  'H99F12A',
  'H99F18A',
  'E10G3A',
  'E16G6A',
  'E16G6AS',
  'E25G5A',
  'E25G5AS',
  'E15G8A',
  'E15G8AS',
  'W99F06A'
]

const _campaignPlanCodeForReduceAgeToRender = ['WLNP85']

const _campaignPlanCodeToModify = {
  WLNP85: 'ICARE',
}
const _returnCampaignPlanCode = ['HEALTHTOPPING-WLNP60L', 'IHEALTHYULTRA-PWLNP85', 'IHEALTHYULTRA-PWLNP60']

//TODO: write test
const _modifyCampaignPlanCode = (basicPlanCode, basicPlanQuery) => {
  if (_isCampaignAndMatchPlanCode(basicPlanCode, basicPlanQuery) && getToggles().ENABLE_ICARE_REPRICING === true) {
    return _campaignPlanCodeToModify[basicPlanCode] || basicPlanCode
  } else if (_returnCampaignPlanCode.includes(basicPlanQuery.code) && basicPlanQuery.type === 'campaign') {
    return basicPlanQuery.code
  } else {
    return basicPlanCode
  }
}

const _isCampaignAndMatchPlanCode = (basicPlanCode, basicPlanQuery) =>
  _campaignPlanCodeForReduceAgeToRender.includes(basicPlanCode) && basicPlanQuery.type === 'campaign'

const _isReduceAgeBeforeRender = (basicPlanCode, basicPlanQuery) => {
  const planCode = _modifyCampaignPlanCode(basicPlanCode, basicPlanQuery)
  return _plancodeForReduceAgeToRender.includes(planCode)
}

const generateYearEndPolicyValueGLTSP = ({
  yearEnd,
  age,
  gender,
  basicSumAssured,
  validSelectedRiders,
  basicPlanQuery,
  cashValueRates,
  coverage,
}) => {
  const surrenderCash = getSurrenderCashMRTA({
    basicPlanQuery,
    gender,
    age,
    yearEnd,
    cashValueRates,
    basicSumAssured,
    validSelectedRiders,
  })

  const yearBasicSumAssured = round(coverage >= yearEnd ? basicSumAssured : 0, 0)

  const riderSumAssured = 0

  return {
    yearEndPolicy: yearEnd,
    basicSumAssured: yearBasicSumAssured,
    riderSumAssured: riderSumAssured,
    surrenderCash: surrenderCash,
  }
}

const generateYearEndPolicyValueMRTA = ({
  yearEnd,
  age,
  gender,
  basicSumAssured,
  validSelectedRiders,
  basicPlanQuery,
  basicPlan,
  cashValueRates,
  SAyear,
  filterSaRate,
  coverage,
}) => {
  const surrenderCash = getSurrenderCashMRTA({
    basicPlanQuery,
    gender,
    age,
    yearEnd,
    cashValueRates,
    basicSumAssured,
    validSelectedRiders,
  })

  const yearBasicSumAssured = getSumAssuredMRTA({
    basicSumAssured,
    SAyear,
    yearEnd,
    filterSaRate,
  })

  const riderSumAssured = round(
    coverage >= yearEnd && yearEnd !== 0 ? validSelectedRiders.reduce((sum, { sumAssured }) => sum + sumAssured, 0) : 0,
    0
  )

  return {
    yearEndPolicy: yearEnd,
    basicSumAssured: yearBasicSumAssured,
    riderSumAssured: riderSumAssured,
    surrenderCash: surrenderCash,
  }
}

export const _generatePolicyValuesForMRTA = (policyValueParams, basicPlan, cashValueRates, saRates) => {
  const { age, gender, validSelectedRiders, basicPlanQuery, loan } = policyValueParams
  const yearEndPeriod = _.get(loan, 'coveragePlan.coverage.term.value', 0) + 1
  const yearEndPeriodRange = _.range(0, yearEndPeriod)

  const basicSumAssured = loan.coveragePlan.loan.sumAssuredContract

  const coverage = loan.coveragePlan.coverage.term.value
  const filterSaRate = findSaRate(basicPlan.code, saRates)
  let SAyear = loan.coveragePlan.coverage.term.value
  if (basicPlanQuery.code === VALUES.MRTA_CODE.MRTA_TRUNCATE) {
    SAyear = loan.coveragePlan.loan.term.value
  }
  let yearEndPolicyValues
  if (isGLTSPGroup(basicPlanQuery.code)) {
    yearEndPolicyValues = yearEndPeriodRange.reduce((previousValue, yearEnd) => {
      return [
        ...previousValue,
        generateYearEndPolicyValueGLTSP({
          yearEnd,
          age,
          gender,
          basicSumAssured,
          validSelectedRiders,
          basicPlanQuery,
          cashValueRates,
          coverage,
        }),
      ]
    }, [])
  } else {
    yearEndPolicyValues = yearEndPeriodRange.reduce((previousValue, yearEnd) => {
      return [
        ...previousValue,
        generateYearEndPolicyValueMRTA({
          yearEnd,
          age,
          gender,
          basicSumAssured,
          validSelectedRiders,
          basicPlanQuery,
          basicPlan,
          cashValueRates,
          SAyear,
          filterSaRate,
          coverage,
        }),
      ]
    }, [])
  }

  return { yearEndPolicyValues }
}

export const _isMonthlyDisplayPremiumRider = (props) => {
  const { monthEndPolicy, modelFactorPeriod, pensionType, age, basicPlanCode, basicCoveragePeriod } = props

  if (VALUES.BUMNAN_READY_CODE_GROUP.includes(basicPlanCode) && age > basicCoveragePeriod.value) {
    return false
  }

  if (pensionType === VALUES.PENSION_ANNUALLY) {
    return true
  }
  switch (modelFactorPeriod) {
    case VALUES.MODEL_FACTOR_PERIOD_MONTHLY:
      return VALUES.RIDER_PREMIUM_DISPLAY_MONTHLY.includes(monthEndPolicy)
    case VALUES.MODEL_FACTOR_PERIOD_QUARTERLY:
      return VALUES.RIDER_PREMIUM_DISPLAY_QUATERLY.includes(monthEndPolicy)
    case VALUES.MODEL_FACTOR_PERIOD_SEMI_ANNUAL:
      return VALUES.RIDER_PREMIUM_DISPLAY_SEMI_ANNUALLY.includes(monthEndPolicy)
    case VALUES.MODEL_FACTOR_PERIOD_ANNUAL:
      return VALUES.RIDER_PREMIUM_DISPLAY_ANNUALLY.includes(monthEndPolicy)
  }
  return false
}

export const _isWithinBasicPlanCoveragePeriod = (yearEnd, coveragePeriod, initialInsuredAge) => {
  switch (coveragePeriod.type) {
    case VALUES.PERIOD_LIMITED_TO_AGE:
      return yearEnd <= coveragePeriod.value - initialInsuredAge.value
    case VALUES.PERIOD_BY_PACKAGE:
    case VALUES.PERIOD_LIMITED_BY_DURATION:
    default:
      return yearEnd <= coveragePeriod.value
  }
}

export const _isRepeatCumulativeBasicPremium = (basicPlan) => {
  switch (basicPlan.category) {
    case 'LIFE_SAVE_PRO':
    case 'RETIREMENT':
    case 'HEALTH_TOPPING':
    case 'LIFE_READY':
    case 'BUMNAN_READY':
    case 'TERM_LIFE':
      return true
    case 'WHOLE_LIFE':
      return (
        isLifeLegacyProduct(basicPlan.code) ||
        isLifeProtectProduct(basicPlan.code) ||
        isWholeLife12PLProduct(basicPlan.code) ||
        isLifeEnsureProduct(basicPlan.code) ||
        isS7Product(basicPlan.code)
      )
    default:
      return false
  }
}

export const _isWithinBasicPlanPaymentPeriod = (yearEnd, paymentPeriod, initialInsuredAge) => {
  switch (paymentPeriod.type) {
    case VALUES.PERIOD_LIMITED_TO_AGE:
      return yearEnd <= paymentPeriod.value - initialInsuredAge.value
    case VALUES.PERIOD_BY_PACKAGE:
    case VALUES.PERIOD_LIMITED_BY_DURATION:
    default:
      return yearEnd <= paymentPeriod.value
  }
}

export const generateRPU_ETI = ({ yearEnd, gender, initialInsuredAge, basicSumAssured, RPURate, ETIRate, PENRate }) => {
  return (result) => {
    const queriedRPURate = queryRPURate(gender, initialInsuredAge, yearEnd, RPURate)
    const queriedPENRate = queryPENRate(gender, initialInsuredAge, yearEnd, PENRate)
    const queriedETIRate = queryETIRate(gender, initialInsuredAge, yearEnd, ETIRate)

    let currentRPU = calculateRPU(queriedRPURate, basicSumAssured)
    let currentETI = calculateETI(queriedETIRate, queriedPENRate, basicSumAssured)

    return {
      ...result,
      ETIMaturity: currentETI.ETIMaturity,
      ETICashpaid: currentETI.ETICashpaid,
      ETIYear: currentETI.ETIYear,
      ETIDay: currentETI.ETIDay,
      RPUSumAssured: currentRPU.RPUSumAssured,
      RPUCashpaid: currentRPU.RPUCashpaid,
    }
  }
}

export const generateDividend = ({
  yearEnd,
  gender,
  initialInsuredAge,
  dividendRate,
  basicPremium,
  cumulativeBasicPremium,
  basicPlan,
  cashValueRates,
  basicSumAssured,
}) => {
  return (result) => {
    const parForSurrender = queryDividendRate(gender, initialInsuredAge, yearEnd, dividendRate)
    const accumulateBasicPremium = cumulativeBasicPremium ? cumulativeBasicPremium : basicPremium
    const surrenderCash = getSurrenderCash({
      basicPlan,
      gender,
      initialInsuredAge,
      yearEnd,
      cashValueRates,
      basicSumAssured,
    })

    const deathBenefit = calculateDeathBenefit({
      basicPlan,
      basicSumAssured,
      cumulativeBasicPremium: accumulateBasicPremium,
      surrenderCash,
      yearEnd,
    })

    const dividendValue = VALUES.GLOBAL_INDEX_16_6_VALUE_INDEX_GROWTH.map((idxGrowth) => {
      const currentIndex = calculateCurrentIndex(yearEnd, idxGrowth)
      const nonGuaranteeDividend = calculateDividendGlobalIndexProduct(currentIndex, accumulateBasicPremium)
      const nonGuaranteeDividendSurrender = calculateDividendSurrender(nonGuaranteeDividend, parForSurrender)
      const deathBenefitPlusNonGuaranteeDividend = yearEnd === 1 ? deathBenefit : deathBenefit + nonGuaranteeDividend
      const surrenderCashPlusNonGuaranteeDividendSurrender = surrenderCash + nonGuaranteeDividendSurrender

      return {
        title: idxGrowth.toString(),
        currentIndex,
        nonGuaranteeDividend,
        nonGuaranteeDividendSurrender,
        deathBenefitPlusNonGuaranteeDividend,
        surrenderCashPlusNonGuaranteeDividendSurrender,
      }
    })

    return {
      ...result,
      dividend: {
        parForSurrender,
        dividendValue,
      },
    }
  }
}

export const generateCashDrop = ({
  yearEnd,
  riders,
  basicPlan,
  basicPremium,
  cumulativeBasicPremium,
  gender,
  yearEndAge,
  occupationFactorForRider,
  modelFactor,
  cashValueRates,
  cashDropRate,
  basicSumAssured,
  modelFactorPeriod,
  riderPremiumRatesPayload,
  initialInsuredAge,
  payerAge,
  payerGender,
  previousYearEndValue,
}) => {
  //prepare the rate
  return (result) => {
    const queriedCashDropRate = queryCashDropRate(gender, initialInsuredAge, yearEnd, cashDropRate)

    const previousCashDrop = _.get(previousYearEndValue, 'cashDrop', 0)
    const previousSumCashDrop = _.get(previousYearEndValue, 'sumCashDrop', 0)
    const previousLowSavingCashDrop = _.get(previousYearEndValue, 'lowSavingCashDrop', 0)
    const previousMediumSavingCashDrop = _.get(previousYearEndValue, 'mediumSavingCashDrop', 0)
    const previousHighSavingCashDrop = _.get(previousYearEndValue, 'highSavingCashDrop', 0)

    let currentCashDrop = calculateCashDrop(queriedCashDropRate, basicSumAssured)
    let sumCashDrop
    let lowSavingCashDrop
    let mediumSavingCashDrop
    let highSavingCashDrop

    const yearEndPolicy =
      basicPlan.coveragePeriod.type === VALUES.PERIOD_LIMITED_BY_DURATION ? yearEnd : yearEndAge.value
    if (
      (basicPlan.category === 'TERM_LIFE' || basicPlan.category === 'INDEX_LINKED') &&
      yearEndPolicy === basicPlan.coveragePeriod.value
    ) {
      const accumulateBasicPremium = d(cumulativeBasicPremium)
        .times(1.01)
        .toNumber()
      const surrenderCash = getSurrenderCash({
        basicPlan,
        gender,
        initialInsuredAge,
        yearEnd,
        cashValueRates,
        basicSumAssured,
      })

      if (VALUES.ULTIMATE_GROWTH_CODE_GROUP.includes(basicPlan.code)) {
        currentCashDrop = Math.max(currentCashDrop, accumulateBasicPremium)
      } else if (
        Object.values(VALUES.GLOBAL_INDEX_16_6_CODE).includes(basicPlan.code) ||
        Object.values(VALUES.INDEX_LINKED_25_5_CODE).includes(basicPlan.code) ||
        Object.values(VALUES.INDEX_LINKED_15_8_CODE).includes(basicPlan.code)
      ) {
        currentCashDrop = currentCashDrop
      } else {
        currentCashDrop = Math.max(currentCashDrop, accumulateBasicPremium, surrenderCash)
      }
    }

    const cumulativeDeathBenefit = d(result.deathBenefit)
      .plus(previousSumCashDrop)
      .toNumber()

    let surrenderCash = result.surrenderCash
    sumCashDrop = d(previousSumCashDrop)
      .plus(currentCashDrop)
      .toNumber()
    lowSavingCashDrop = d(previousLowSavingCashDrop)
      .times(1.005)
      .plus(currentCashDrop)
      .toNumber()
    mediumSavingCashDrop = d(previousMediumSavingCashDrop)
      .times(1.015)
      .plus(currentCashDrop)
      .toNumber()
    highSavingCashDrop = d(previousHighSavingCashDrop)
      .times(1.025)
      .plus(currentCashDrop)
      .toNumber()

    if (
      yearEndAge.value === basicPlan.coveragePeriod.value &&
      !['LIFE_SAVE_PRO', 'TERM_LIFE'].includes(_.get(basicPlan, 'category'))
    ) {
      currentCashDrop = d(currentCashDrop)
        .plus(basicSumAssured)
        .toNumber()
      lowSavingCashDrop = d(lowSavingCashDrop)
        .plus(basicSumAssured)
        .toNumber()
      mediumSavingCashDrop = d(mediumSavingCashDrop)
        .plus(basicSumAssured)
        .toNumber()
      highSavingCashDrop = d(highSavingCashDrop)
        .plus(basicSumAssured)
        .toNumber()
      surrenderCash = basicSumAssured
    }

    return {
      ...result,
      cashDrop: currentCashDrop,
      sumCashDrop,
      lowSavingCashDrop,
      mediumSavingCashDrop,
      highSavingCashDrop,
      cumulativeDeathBenefit,
      surrenderCash,
    }
  }
}

const _is12PL = (code) => code === '12PL'

const getGenerateAdditionalYearEndPolicyValueFor12PL = ({
  basicPlan,
  cashDropRates,
  initialInsuredAge,
  yearEnd,
  gender,
  basicSumAssured,
  previousYearEndValue,
  yearEndAge,
  cashValueRates,
}) => {
  const cashDropRateNonGuarantee = findNonGuaranteeCashDropRate(basicPlan.code, cashDropRates)

  const cashDropRateGuarantee = findCashDropRate(basicPlan.code, cashDropRates)

  return (yearEndPolicyValue) => {
    const queriedNonGuaranteeCashDropRate = queryCashDropRate(
      gender,
      initialInsuredAge,
      yearEnd,
      cashDropRateNonGuarantee
    )

    const surrenderCash = getSurrenderCash({
      basicPlan,
      gender,
      initialInsuredAge,
      yearEnd,
      cashValueRates,
      basicSumAssured,
    })

    const queriedGuaranteeCashDropRate = queryCashDropRate(gender, initialInsuredAge, yearEnd, cashDropRateGuarantee)

    const nonGuaranteeCashDrop = calculateGuarenteeDividend({
      sumAssured: basicSumAssured,
      rate: queriedNonGuaranteeCashDropRate,
    })
    const guaranteeDividend = calculateGuarenteeDividend({
      sumAssured: basicSumAssured,
      rate: queriedGuaranteeCashDropRate,
    })

    const maturityAmount = calculateMaturity({ basicSumAssured, maturityRate: 1150 })
    const guaranteeDividendFinal =
      yearEndAge.value === basicPlan.coveragePeriod.value ? guaranteeDividend + maturityAmount : guaranteeDividend

    const nonGuaranteeDividendDeposit = calculateInterestNonGuaranteeDividendDeposit({
      previousNonGuranteeDividendDeposit: _.get(previousYearEndValue, 'nonGuaranteeDividendDeposit', 0),
      guaranteeDividendOfYear: guaranteeDividendFinal,
      interestRate: 1.005,
    })

    const cumulativeGuaranteeCashDrop = d(_.get(previousYearEndValue, 'cumulativeGuaranteeCashDrop', 0))
      .plus(guaranteeDividend)
      .toNumber()

    const differenceDividend = d(nonGuaranteeCashDrop)
      .minus(guaranteeDividend)
      .toNumber()
    const lowInterest = d(differenceDividend)
      .times(0)
      .div(100)
      .toNumber()
    const lowInterestDeposit = d(_.get(previousYearEndValue, 'lowInterestDeposit', 0))
      .times(1.005)
      .plus(lowInterest)
      .toNumber()
    const midInterest = d(differenceDividend)
      .times(75)
      .div(100)
      .toNumber()
    const midInterestDeposit = d(_.get(previousYearEndValue, 'midInterestDeposit', 0))
      .times(1.005)
      .plus(midInterest)
      .toNumber()
    const highInterest = d(differenceDividend)
      .times(90)
      .div(100)
      .toNumber()
    const highInterestDeposit = d(_.get(previousYearEndValue, 'highInterestDeposit', 0))
      .times(1.005)
      .plus(highInterest)
      .toNumber()
    const totalLowDividend = d(nonGuaranteeDividendDeposit)
      .plus(lowInterestDeposit)
      .toNumber()
    const totalMidDividend = d(nonGuaranteeDividendDeposit)
      .plus(midInterestDeposit)
      .toNumber()
    const totalHighDividend = d(nonGuaranteeDividendDeposit)
      .plus(highInterestDeposit)
      .toNumber()
    const totalBenefit = d(_.get(previousYearEndValue, 'cumulativeGuaranteeCashDrop', 0))
      .plus(yearEndPolicyValue.deathBenefit)
      .toNumber()

    return {
      ...yearEndPolicyValue,
      surrenderCash,
      cashDrop: guaranteeDividendFinal, //TODO override exists and waiting refactor
      sumCashDrop: nonGuaranteeDividendDeposit, //TODO override exists and waiting refactor
      nonGuaranteeCashDrop,
      guaranteeDividend: guaranteeDividendFinal,
      nonGuaranteeDividendDeposit,
      cumulativeGuaranteeCashDrop,
      maturityAmount,
      lowInterest,
      lowInterestDeposit,
      midInterest,
      midInterestDeposit,
      highInterest,
      highInterestDeposit,
      totalLowDividend,
      totalMidDividend,
      totalHighDividend,
      totalBenefit,
    }
  }
}

const generateTotalFromYearEndPolicyValues = ({ yearEndPolicyValues, cashDropRate }) => {
  const subtotalAnnualRiderPremium = sumPolicyValuesByColumn(yearEndPolicyValues, 'annualRiderPremium')
  const subtotalAnnualBasicPremium = sumPolicyValuesByColumn(yearEndPolicyValues, 'annualBasicPremium')
  const lastYearEndPolicyValue = _.last(yearEndPolicyValues)
  const subtotalCashDrop = cashDropRate ? sumPolicyValuesByColumn(yearEndPolicyValues, 'cashDrop') : 0
  return {
    subtotalAnnualRiderPremium,
    subtotalAnnualBasicPremium,
    subtotalCashDrop,
    subtotalLowSavingCashDrop: round(_.get(lastYearEndPolicyValue, 'lowSavingCashDrop', 0)),
    subtotalMediumSavingCashDrop: round(_.get(lastYearEndPolicyValue, 'mediumSavingCashDrop', 0)),
    subtotalHighSavingCashDrop: round(_.get(lastYearEndPolicyValue, 'highSavingCashDrop', 0)),
    grandTotalPremium: d(subtotalAnnualRiderPremium)
      .plus(subtotalAnnualBasicPremium)
      .toNumber(),
  }
}

const generateTotalFromYearEndPolicyValuesFor12PL = ({ yearEndPolicyValues, cashDropRate }) => {
  const subtotalAnnualRiderPremium = sumPolicyValuesByColumn(yearEndPolicyValues, 'annualRiderPremium')
  const subtotalAnnualBasicPremium = sumPolicyValuesByColumn(yearEndPolicyValues, 'annualBasicPremium')
  const lastYearEndPolicyValue = _.last(yearEndPolicyValues)
  return {
    subtotalAnnualRiderPremium,
    subtotalAnnualBasicPremium,
    subtotalGuaranteeDividend: sumPolicyValuesByColumn(yearEndPolicyValues, 'guaranteeDividend'),
    subTotalNonGuaranteeDividendDeposit: _.get(lastYearEndPolicyValue, 'nonGuaranteeDividendDeposit'),
    subTotalLowInterest: sumPolicyValuesByColumn(yearEndPolicyValues, 'lowInterest'),
    subTotalLowInterestDeposit: _.get(lastYearEndPolicyValue, 'lowInterestDeposit'),
    subTotalMidInterest: sumPolicyValuesByColumn(yearEndPolicyValues, 'midInterest'),
    subTotalMidInterestDeposit: _.get(lastYearEndPolicyValue, 'midInterestDeposit'),
    subTotalHighInterest: sumPolicyValuesByColumn(yearEndPolicyValues, 'highInterest'),
    subTotalHighInterestDeposit: _.get(lastYearEndPolicyValue, 'highInterestDeposit'),
    subTotalLowDividend: _.get(lastYearEndPolicyValue, 'totalLowDividend'),
    subTotalMidDividend: _.get(lastYearEndPolicyValue, 'totalMidDividend'),
    subTotalHighDividend: _.get(lastYearEndPolicyValue, 'totalHighDividend'),
    grandTotalPremium: d(subtotalAnnualRiderPremium)
      .plus(subtotalAnnualBasicPremium)
      .toNumber(),
  }
}

const getGenerateTotalFromYearEndPolicyValues = ({ basicPlan, yearEndPolicyValues, cashDropRate }) => {
  const code = basicPlan.code
  switch (code) {
    case '12PL':
      return generateTotalFromYearEndPolicyValuesFor12PL({ yearEndPolicyValues, cashDropRate })
    default:
      return generateTotalFromYearEndPolicyValues({ yearEndPolicyValues, cashDropRate })
  }
}

export const _getMessageUnderiShieldPolicyValueTable = ({ policyValue, basicPlan }) => {
  const firstRow = _findFirstYearEndPolicyValueWhereDeathBenefitExceedsBasicSA(policyValue.yearEndPolicyValues)

  // iShield codes start with 'WLCI'
  if (basicPlan.code.match('^WLCI.+') && firstRow) {
    const { age, yearEndPolicy } = firstRow
    return Mustache.render(MESSAGES.ISHIELD_POLICY_TABLE_INFO, {
      age: age.value,
      yearEndPolicy: yearEndPolicy,
    })
  } else {
    return null
  }
}

export const _findFirstYearEndPolicyValueWhereDeathBenefitExceedsBasicSA = (yearEndVal) => {
  const filtered = yearEndVal.filter((row) => row.deathBenefit > row.basicSumAssured)
  const sorted = _.sortBy(filtered, (item) => item.yearEndPolicy)
  return sorted[0] // first matching item
}

/**
 * createBenefitIllustrationPolicyValueService
 * @param dataWrapper {ReturnType<import('core/data/data-wrapper')._DB>}
 * @param benefitIllustrationYearEndRiderPremiumService {ReturnType<import('core/service/benefit-illustration/year-end-rider-premium').createBenefitIllustrationYearEndRiderPremiumService>}
 * @param displayProductService {ReturnType<import('core/service/display-product/display-product').createDisplayProductService>}
 * @param insuredOccupationService {ReturnType<import('core/service/insured/occupation').createInsuredOccupationService>}
 */
export function createBenefitIllustrationPolicyValueService(
  dataWrapper,
  benefitIllustrationYearEndRiderPremiumService,
  displayProductService,
  insuredOccupationService
) {
  const _generatePensionEndYear = (props) => {
    const {
      yearEnd,
      yearEndAge,
      basicPlan,
      pensionType,
      presentValues,
      basicSumAssured,
      cumulativeBasicPremium,
      monthEndPolicy,
      previousYearEndValue,
      riderPremiumRatesPayload,
      initialInsuredAge,
      payerAge,
      payerGender,
      gender,
      basicPremium,
      occupationFactorForRider,
      modelFactor,
      modelFactorPeriod,
      riders,
      payerOccupationFactorForRider,
      userAgentType,
      pensionAge,
    } = props
    const reductYearAge = yearEndAge.value - 1
    const presentRate = getPresentRate(basicPlan.code, pensionType, presentValues)
    const presentValue = calculatePresentValue({
      sumAssured: basicSumAssured,
      presentRate,
      year: reductYearAge,
      month: monthEndPolicy,
      pensionType,
    })
    const pensionAmount = calculatePensionAmount(basicSumAssured, pensionType, basicPlan.code, reductYearAge)
    const cumulativePersionAmount = d(_.get(previousYearEndValue, 'cumulativePersionAmount', 0)).plus(pensionAmount)

    const pensionAmountBalanceAtBasicPremium = d(cumulativeBasicPremium).minus(cumulativePersionAmount)

    let pensionTotal
    if (VALUES.BUMNAN_READY_CODE_GROUP.includes(basicPlan.code)) {
      pensionTotal = getPensionTotalBumnanReady(
        presentValue,
        pensionAmountBalanceAtBasicPremium,
        reductYearAge,
        cumulativeBasicPremium,
        cumulativePersionAmount
      )
    } else if (VALUES.BUMNAN_SMART_GROUP.includes(basicPlan.code)) {
      pensionTotal = getPensionTotalBumnanSmart(
        presentValue,
        pensionAmountBalanceAtBasicPremium,
        reductYearAge,
        cumulativeBasicPremium,
        cumulativePersionAmount,
        pensionAge
      )
    } else {
      pensionTotal = getPensionTotal(presentValue, pensionAmountBalanceAtBasicPremium)
    }

    const basicPaymentPeriod = basicPlan.paymentPeriod
    const basicCoveragePeriod = basicPlan.coveragePeriod
    let yearEndRiderPremium = 0
    const isSumYearEnd = pensionType === 'annually'

    if (
      _isMonthlyDisplayPremiumRider({
        monthEndPolicy: monthEndPolicy,
        modelFactorPeriod: modelFactorPeriod,
        pensionType: pensionType,
        age: yearEndAge.value,
        basicPlanCode: basicPlan.code,
        basicCoveragePeriod,
      })
    ) {
      yearEndRiderPremium = benefitIllustrationYearEndRiderPremiumService.calculateTotalRiderPremium(
        riderPremiumRatesPayload,
        basicPlan,
        basicPremium,
        initialInsuredAge.value,
        payerAge,
        payerGender,
        gender,
        occupationFactorForRider,
        modelFactor,
        modelFactorPeriod,
        basicPaymentPeriod,
        basicCoveragePeriod,
        yearEndAge.value - 1,
        isSumYearEnd,
        payerOccupationFactorForRider,
        userAgentType
      )(riders)
    }

    const annualRiderPremium = roundDown(yearEndRiderPremium, 2)

    return {
      age: yearEndAge,
      yearEndPolicy: yearEnd,
      cumulativeBasicPremium,
      annualBasicPremium: 0,
      annualRiderPremium: annualRiderPremium,
      annualTotalPremium: annualRiderPremium,
      surrenderCash: 0,
      deathBenefit: 0,
      basicSumAssured: 0,
      cumulativePersionAmount,
      pensionPerYear: pensionAmount,
      pensionTotal,
    }
  }

  const _generatePension = (props) => {
    const { pensionType, previousYearEndValue } = props
    if (pensionType === 'monthly') {
      const months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
      let tempPreviousYearEndValue = previousYearEndValue
      return months.map((month) => {
        const policyEndYear = _generatePensionEndYear({
          ...props,
          previousYearEndValue: tempPreviousYearEndValue,
          monthEndPolicy: month,
        })
        tempPreviousYearEndValue = policyEndYear
        return { ...policyEndYear, monthEndPolicy: month }
      })
    }

    return [
      _generatePensionEndYear({
        ...props,
        monthEndPolicy: 1,
      }),
    ]
  }

  const _generatePolicyValuesForRetirement = (
    policyValueParams,
    basicPlan,
    cashValueRates,
    riderPremiumRatesPayload,
    occupationFactorForRider,
    cashDropRates,
    presentValues,
    payerOccupationFactorForRider
  ) => {
    const {
      age,
      gender,
      sumAssured,
      basicPremium,
      fullBasicPremium,
      fullAnnualBasicPremium,
      modelFactorPeriod,
      validSelectedRiders,
      payerAge,
      payerGender,
      modelFactor,
      pensionType = '',
      userAgentType,
      pensionAge,
      basicPlanQuery,
    } = policyValueParams
    const initialAge = age.unit === 'year' ? age.value : 0
    const ageRangeWithinCoveragePeriod = _.range(
      initialAge + 1,
      // +2 because the first year of coverage we don't care when the customer buy,
      // we kindly give them for one more year pension amount
      // if you see in the Excel file when insured age 35 the number of coverage ( last record ) will show 51
      basicPlan.coveragePeriod.value + 2
    )

    const cashDropRate = findCashDropRate(basicPlan.code, cashDropRates)
    const generateCVTable = compose((item) => {
      item.monthEndPolicy = 1
      return [item]
    }, generateYearEndPolicyValue)
    let updateValidSelectedRiders = validSelectedRiders

    const pensionAgeSelected = VALUES.BUMNAN_SMART_GROUP.includes(basicPlanQuery.basicPlanCode) ? pensionAge : 60

    const yearEndPolicyValues = ageRangeWithinCoveragePeriod.reduce((previousValue, yearEndAge) => {
      const yearEnd = yearEndAge - initialAge

      let yearBasicPremium = basicPremium
      let annualBasicPremium = calculateAnnualBasicPremium({
        basicPremium: yearBasicPremium,
        modelFactorPeriod,
      })

      if (fullBasicPremium && fullAnnualBasicPremium && basicPremium !== fullBasicPremium && yearEnd !== 1) {
        yearBasicPremium = fullBasicPremium
        annualBasicPremium = calculateAnnualBasicPremium({
          basicPremium: yearBasicPremium,
          modelFactorPeriod,
        })
        // due to after discount price will be same at latest so the data will not set back
        // MRTA IGROW(PBG) is not apply so it will set with defaults
        updateValidSelectedRiders = validSelectedRiders.map((rider) => {
          if (_.get(rider, 'options.0.source') === VALUES.SOURCE_TOTAL_BASIC_ANNUAL_PREMIUM_WITH_LIMITED_RANGE) {
            return { ...rider, sumAssured: fullAnnualBasicPremium }
          }
          return rider
        })
      }

      let cumulativeBasicPremium = _accumulateBasicPremium(age, yearEnd, basicPlan.paymentPeriod, d(annualBasicPremium))
      if (fullBasicPremium && basicPremium !== fullBasicPremium && yearEnd !== 1) {
        let annualFirstYearPremium = calculateAnnualBasicPremium({
          basicPremium: basicPremium,
          modelFactorPeriod,
        })
        cumulativeBasicPremium = cumulativeBasicPremium - annualBasicPremium + annualFirstYearPremium
      }

      const policyValues =
        yearEndAge <= pensionAgeSelected
          ? generateCVTable({
              yearEnd,
              riders: updateValidSelectedRiders,
              basicPlan,
              basicPremium: yearBasicPremium,
              cumulativeBasicPremium,
              gender,
              yearEndAge: {
                value: yearEndAge,
                unit: 'year',
              },
              occupationFactorForRider,
              modelFactor,
              cashValueRates,
              cashDropRate,
              basicSumAssured: sumAssured,
              modelFactorPeriod,
              riderPremiumRatesPayload,
              initialInsuredAge: {
                value: initialAge,
                unit: 'year',
              },
              payerAge,
              payerGender,
              previousYearEndValue: previousValue[previousValue.length - 1],
              payerOccupationFactorForRider,
              userAgentType,
            })
          : _generatePension({
              yearEnd,
              riders: updateValidSelectedRiders,
              basicPlan,
              basicPremium: yearBasicPremium,
              cumulativeBasicPremium,
              gender,
              yearEndAge: {
                value: yearEndAge,
                unit: 'year',
              },
              occupationFactorForRider,
              modelFactor,
              cashValueRates,
              cashDropRate,
              basicSumAssured: sumAssured,
              modelFactorPeriod,
              riderPremiumRatesPayload,
              initialInsuredAge: {
                value: initialAge,
                unit: 'year',
              },
              payerAge,
              payerGender,
              previousYearEndValue: previousValue[previousValue.length - 1],
              pensionType,
              presentValues,
              payerOccupationFactorForRider,
              userAgentType,
              pensionAge,
            })
      return [...previousValue, ...policyValues]
    }, [])

    const sumBy = (policyValues, key) => {
      return _.map(policyValues, key)
        .reduce((a, b) => a.add(b || 0), d(0))
        .toNumber()
    }

    const subtotalAnnualRiderPremium = sumBy(yearEndPolicyValues, 'annualRiderPremium')

    const subtotalPensionAmount = sumBy(yearEndPolicyValues, 'pensionPerYear')
    const subtotalAnnualBasicPremium = sumBy(yearEndPolicyValues, 'annualBasicPremium')
    const lastYearEndPolicyValue = _.last(yearEndPolicyValues)
    const subtotalCashDrop = cashDropRate ? sumBy(yearEndPolicyValues, 'cashDrop') : 0

    const cashFlowArrayIRR = yearEndPolicyValues.map((item) => {
      return item.age.value <= pensionAgeSelected
        ? item.annualBasicPremium === 0
          ? 0
          : -item.annualBasicPremium
        : item.pensionPerYear
    })

    let dateCashFlowArrayIRR
    if (pensionType === VALUES.PENSION_MONTHLY) {
      const startDate = new Date()
      const day = startDate.getDate()
      const month = startDate.getMonth()
      const year = startDate.getFullYear()

      let currentDate = new Date(year, month, day)

      dateCashFlowArrayIRR = yearEndPolicyValues.map((item) => {
        const isBeforePension = item.age.value <= pensionAgeSelected
        const dateToReturn = new Date(currentDate)
        if (isBeforePension) {
          currentDate.setFullYear(currentDate.getFullYear() + 1)
        } else {
          currentDate.setMonth(currentDate.getMonth() + 1)
        }
        return dateToReturn
      })
    }

    const irrValue =
      pensionType === VALUES.PENSION_MONTHLY
        ? calculateXIRR(cashFlowArrayIRR, dateCashFlowArrayIRR)
        : calculateIRR(cashFlowArrayIRR)

    const finalIRR = round(irrValue * 100, 4).toFixed(2)

    return {
      yearEndPolicyValues,
      subtotalAnnualRiderPremium,
      subtotalAnnualBasicPremium,
      subtotalCashDrop,
      subtotalPensionAmount,
      subtotalLowSavingCashDrop: round(_.get(lastYearEndPolicyValue, 'lowSavingCashDrop', 0)),
      subtotalMediumSavingCashDrop: round(_.get(lastYearEndPolicyValue, 'mediumSavingCashDrop', 0)),
      subtotalHighSavingCashDrop: round(_.get(lastYearEndPolicyValue, 'highSavingCashDrop', 0)),
      grandTotalPremium: d(subtotalAnnualRiderPremium)
        .plus(subtotalAnnualBasicPremium)
        .toNumber(),
      IRR: finalIRR,
    }
  }

  const generatePolicyValues = async (policyValueParams) => {
    const basicPlan = await displayProductService.getBasicPlan(policyValueParams.basicPlanQuery)
    const basicPlanCategory = _.get(basicPlan, 'category')
    const code = _.get(policyValueParams, 'basicPlanQuery.code')
    const riderPremiumRatesPayload = await dataWrapper.getRiderPremiumRates()
    const riderOccupationFactors = await insuredOccupationService.getRiskiestOccupationFactorForRiders(
      policyValueParams.basicPlanQuery,
      policyValueParams.natureOfDutyCodes,
      policyValueParams.validSelectedRiders
    )
    const payerOccupationFactorForRider = await insuredOccupationService.getRiskiestOccupationFactorForRiders(
      policyValueParams.basicPlanQuery,
      policyValueParams.payerNatureOfDutyCodes,
      policyValueParams.validSelectedRiders
    )
    const cashValueRates = await dataWrapper.getCashValueRates()
    const cashDropRates = await dataWrapper.getCashDropRates()
    const dividendRates = await dataWrapper.getDividendRates()

    const RPURates = await dataWrapper.getRPURates()
    const ETIRates = await dataWrapper.getETIRates()
    const PENRates = await dataWrapper.getPENRates()
    const SaRates = await dataWrapper.getSaRates()

    const displayProduct = displayProductService.transformToDisplayProduct({
      ...basicPlan,
      riders: basicPlan.riders.map((rider) => rider.code),
    })

    switch (basicPlanCategory) {
      case 'SAVING':
        return _generatePolicyValuesForSaving(
          policyValueParams,
          displayProduct,
          cashValueRates,
          riderPremiumRatesPayload,
          riderOccupationFactors,
          cashDropRates
        )
      case 'MRTA':
        return _generatePolicyValuesForMRTA(policyValueParams, displayProduct, cashValueRates, SaRates)
      case 'LIFE_SAVE_PRO':
      case 'TERM_LIFE':
        return _generatePolicyValuesForLifeSavePro(
          policyValueParams,
          displayProduct,
          cashValueRates,
          riderPremiumRatesPayload,
          riderOccupationFactors,
          cashDropRates,
          payerOccupationFactorForRider,
          dividendRates
        )
      case 'RETIREMENT':
      case 'BUMNAN_READY':
        const presentValues = await dataWrapper.getPresentValues()
        return _generatePolicyValuesForRetirement(
          policyValueParams,
          displayProduct,
          cashValueRates,
          riderPremiumRatesPayload,
          riderOccupationFactors,
          cashDropRates,
          presentValues,
          payerOccupationFactorForRider
        )
      case 'HEALTH':
      case 'HEALTH_TOPPING':
        return VALUES.IHEALTHY_ULTRA_IHEALTHY_CODE.includes(code)
          ? _generatePolicyValues(
              policyValueParams,
              displayProduct,
              cashValueRates,
              riderPremiumRatesPayload,
              riderOccupationFactors,
              cashDropRates,
              payerOccupationFactorForRider
            )
          : _generatePolicyValuesForHealthTopping(
              policyValueParams,
              displayProduct,
              cashValueRates,
              riderPremiumRatesPayload,
              riderOccupationFactors,
              cashDropRates,
              payerOccupationFactorForRider
            )
      default:
        return _generatePolicyValues(
          policyValueParams,
          displayProduct,
          cashValueRates,
          riderPremiumRatesPayload,
          riderOccupationFactors,
          cashDropRates,
          payerOccupationFactorForRider,
          RPURates,
          ETIRates,
          PENRates
        )
    }
  }

  const generateYearEndPolicyValue = ({
    yearEnd,
    riders,
    basicPlan,
    basicPremium,
    cumulativeBasicPremium,
    gender,
    yearEndAge,
    occupationFactorForRider,
    modelFactor,
    cashValueRates,
    cashDropRate,
    basicSumAssured,
    modelFactorPeriod,
    riderPremiumRatesPayload,
    initialInsuredAge,
    payerAge,
    payerGender,
    previousYearEndValue,
    payerOccupationFactorForRider,
    userAgentType,
  }) => {
    const isWithinBasicPlanPaymentPeriod = _isWithinBasicPlanPaymentPeriod(
      yearEnd,
      basicPlan.paymentPeriod,
      initialInsuredAge
    )
    const isRepeatCumulativeBasicPremium = _isRepeatCumulativeBasicPremium(basicPlan)

    const surrenderCash = getSurrenderCash({
      basicPlan,
      gender,
      initialInsuredAge,
      yearEnd,
      cashValueRates,
      basicSumAssured,
    })

    const basicPaymentPeriod = basicPlan.paymentPeriod
    const basicCoveragePeriod = basicPlan.coveragePeriod
    const isSumYearEnd = true
    const yearEndRiderPremium = benefitIllustrationYearEndRiderPremiumService.calculateTotalRiderPremium(
      riderPremiumRatesPayload,
      basicPlan,
      basicPremium,
      initialInsuredAge.value,
      payerAge,
      payerGender,
      gender,
      occupationFactorForRider,
      modelFactor,
      modelFactorPeriod,
      basicPaymentPeriod,
      basicCoveragePeriod,
      yearEndAge.value - 1,
      isSumYearEnd,
      payerOccupationFactorForRider,
      userAgentType
    )(riders)

    const annualBasicPremium = d(roundDown(d(basicPremium).times(d(modelFactorPeriod).toNumber()), 2))
    const annualRiderPremium = roundDown(yearEndRiderPremium, 2)
    const annualBasicPremiumInPaymentPeriod = isWithinBasicPlanPaymentPeriod ? annualBasicPremium.toNumber() : 0
    const annualTotalPremium = d(annualBasicPremiumInPaymentPeriod)
      .plus(d(annualRiderPremium))
      .toNumber()

    // death benefit calculation logic is a candidate which should eventually be
    // moved to product configuration. We hard code it here for now to wait until
    // we understand more about its pattern.
    // This formula is just for iProtect series and the campaign on top of it.

    const deathBenefit = calculateDeathBenefit({
      basicPlan,
      basicSumAssured,
      cumulativeBasicPremium,
      surrenderCash,
      yearEnd,
    })

    return {
      age: yearEndAge,
      yearEndPolicy: yearEnd,
      cumulativeBasicPremium:
        isWithinBasicPlanPaymentPeriod || isRepeatCumulativeBasicPremium ? cumulativeBasicPremium : 0,
      annualBasicPremium: annualBasicPremiumInPaymentPeriod,
      annualRiderPremium,
      annualTotalPremium,
      surrenderCash,
      deathBenefit,
      basicSumAssured,
    }
  }

  const getGenerateYearEndPolicyValue = ({
    basicPlan,
    cashDropRate,
    basicPlanQuery,
    RPURate,
    ETIRate,
    dividendRate,
  }) => {
    return (props) => {
      const mainGenerator = cashDropRate
        ? (props) => generateCashDrop(props)(generateYearEndPolicyValue(props))
        : generateYearEndPolicyValue

      const composedGenerator = compact([
        _isReduceAgeBeforeRender(basicPlan.code, basicPlanQuery) && _reduceAgeToRender,
        _is12PL(basicPlan.code) && getGenerateAdditionalYearEndPolicyValueFor12PL(props),
        (RPURate !== undefined || ETIRate !== undefined) && getToggles().ENABLE_RPU_ETI && generateRPU_ETI(props),
        dividendRate != undefined && generateDividend(props),
        mainGenerator,
      ])

      return compose(composedGenerator)(props)
    }
  }

  const _generatePolicyValuesForLifeSavePro = (
    policyValueParams,
    basicPlan,
    cashValueRates,
    riderPremiumRatesPayload,
    occupationFactorForRider,
    cashDropRates,
    payerOccupationFactorForRider,
    dividendRates
  ) => {
    const {
      age,
      gender,
      sumAssured,
      basicPremium,
      modelFactorPeriod,
      validSelectedRiders,
      payerAge,
      payerGender,
      modelFactor,
      basicPlanQuery,
      userAgentType,
    } = policyValueParams

    const initialAge = age.unit === 'year' ? age.value : 0
    // const ageRangeWithinCoveragePeriod = _.range(initialAge + 1, basicPlan.coveragePeriod.value + initialAge + 1)
    const ageRangeWithinCoveragePeriod = getAgeRangeWithinCoveragePeriod(initialAge, basicPlan.coveragePeriod)
    const cashDropRate = findCashDropRate(basicPlan.code, cashDropRates)
    const dividendRate = findDividendRate(basicPlan.code, dividendRates)
    const generatePolicyValueRow = getGenerateYearEndPolicyValue({
      basicPlan,
      cashDropRate,
      basicPlanQuery,
      dividendRate,
    })

    const yearEndPolicyValues = ageRangeWithinCoveragePeriod.reduce((previousValue, yearEndAge) => {
      const yearEnd = yearEndAge - initialAge

      const annualBasicPremium = d(roundDown(d(basicPremium).times(d(modelFactorPeriod).toNumber()), 2))
      const cumulativeBasicPremium = _accumulateBasicPremium(
        age,
        yearEnd,
        basicPlan.paymentPeriod,
        d(annualBasicPremium)
      )

      return [
        ...previousValue,
        generatePolicyValueRow({
          yearEnd,
          riders: validSelectedRiders,
          basicPlan,
          basicPremium,
          cumulativeBasicPremium,
          gender,
          yearEndAge: {
            value: yearEndAge,
            unit: 'year',
          },
          occupationFactorForRider,
          modelFactor,
          cashValueRates,
          cashDropRates,
          cashDropRate,
          dividendRate,
          basicSumAssured: sumAssured,
          modelFactorPeriod,
          riderPremiumRatesPayload,
          initialInsuredAge: {
            value: initialAge,
            unit: 'year',
          },
          payerAge,
          payerGender,
          previousYearEndValue: previousValue[previousValue.length - 1],
          payerOccupationFactorForRider,
          userAgentType,
        }),
      ]
    }, [])

    const sumBy = (policyValues, key) => {
      return _.map(policyValues, key)
        .reduce((a, b) => a.add(b), d(0))
        .toNumber()
    }

    const subtotalAnnualRiderPremium = sumBy(yearEndPolicyValues, 'annualRiderPremium')
    const subtotalAnnualBasicPremium = sumBy(yearEndPolicyValues, 'annualBasicPremium')
    const lastYearEndPolicyValue = _.last(yearEndPolicyValues)
    const subtotalCashDrop = cashDropRate ? sumBy(yearEndPolicyValues, 'cashDrop') : 0
    const lastDividentValue = _.get(lastYearEndPolicyValue, 'dividend.dividendValue', [])

    return {
      yearEndPolicyValues,
      subtotalAnnualRiderPremium,
      subtotalAnnualBasicPremium,
      subtotalCashDrop,
      subtotalLowSavingCashDrop: round(
        _.get(lastYearEndPolicyValue, 'lowSavingCashDrop', 0),
        decimalPrecision(basicPlan.code)
      ),
      subtotalMediumSavingCashDrop: round(_.get(lastYearEndPolicyValue, 'mediumSavingCashDrop', 0)),
      subtotalHighSavingCashDrop: round(_.get(lastYearEndPolicyValue, 'highSavingCashDrop', 0)),
      subtotalNonGuaranteeDividend: {
        minusonePercent: round(
          _.get(
            lastDividentValue.find((v) => v.title === '-1'),
            'nonGuaranteeDividend',
            0
          )
        ),
        threePercent: round(
          _.get(
            lastDividentValue.find((v) => v.title === '3'),
            'nonGuaranteeDividend',
            0
          )
        ),
        fivePercent: round(
          _.get(
            lastDividentValue.find((v) => v.title === '5'),
            'nonGuaranteeDividend',
            0
          )
        ),
      },
      grandTotalPremium: d(subtotalAnnualRiderPremium)
        .plus(subtotalAnnualBasicPremium)
        .toNumber(),
    }
  }

  const _generatePolicyValuesForHealthTopping = (
    policyValueParams,
    basicPlan,
    cashValueRates,
    riderPremiumRatesPayload,
    occupationFactorForRider,
    cashDropRates,
    payerOccupationFactorForRider
  ) => {
    const {
      age,
      gender,
      sumAssured,
      basicPremium,
      modelFactorPeriod,
      validSelectedRiders,
      payerAge,
      payerGender,
      modelFactor,
      basicPlanQuery,
      userAgentType,
    } = policyValueParams
    const initialAge = age.unit === 'year' ? age.value : 0
    const ageRangeWithinCoveragePeriod = _.range(initialAge, basicPlan.coveragePeriod.value)
    const cashDropRate = findCashDropRate(basicPlan.code, cashDropRates)
    const generatePolicyValueRow = getGenerateYearEndPolicyValue({
      basicPlan,
      cashDropRate,
      basicPlanQuery,
    })

    const yearEndPolicyValues = ageRangeWithinCoveragePeriod.reduce((previousValue, yearEndAge) => {
      const yearEnd = yearEndAge - initialAge + 1
      const annualBasicPremium = calculateAnnualBasicPremium({
        basicPremium,
        modelFactorPeriod,
      })

      const cumulativeBasicPremium = _accumulateBasicPremium(
        age,
        yearEnd,
        basicPlan.paymentPeriod,
        d(annualBasicPremium)
      )

      return [
        ...previousValue,
        generatePolicyValueRow({
          yearEnd,
          riders: validSelectedRiders,
          basicPlan,
          basicPremium,
          cumulativeBasicPremium,
          gender,
          yearEndAge: {
            value: yearEndAge,
            unit: 'year',
          },
          occupationFactorForRider,
          modelFactor,
          cashValueRates,
          cashDropRates,
          cashDropRate,
          basicSumAssured: sumAssured,
          modelFactorPeriod,
          riderPremiumRatesPayload,
          initialInsuredAge: {
            value: initialAge,
            unit: 'year',
          },
          payerAge,
          payerGender,
          previousYearEndValue: previousValue[previousValue.length - 1],
          payerOccupationFactorForRider,
          userAgentType,
        }),
      ]
    }, [])

    const sumBy = (policyValues, key) => {
      return _.map(policyValues, key)
        .reduce((a, b) => a.add(b), d(0))
        .toNumber()
    }

    const subtotalAnnualRiderPremium = sumBy(yearEndPolicyValues, 'annualRiderPremium')
    const subtotalAnnualBasicPremium = sumBy(yearEndPolicyValues, 'annualBasicPremium')
    const lastYearEndPolicyValue = _.last(yearEndPolicyValues)
    const subtotalCashDrop = cashDropRate ? sumBy(yearEndPolicyValues, 'cashDrop') : 0

    return {
      yearEndPolicyValues,
      subtotalAnnualRiderPremium,
      subtotalAnnualBasicPremium,
      subtotalCashDrop,
      subtotalLowSavingCashDrop: round(_.get(lastYearEndPolicyValue, 'lowSavingCashDrop', 0)),
      subtotalMediumSavingCashDrop: round(_.get(lastYearEndPolicyValue, 'mediumSavingCashDrop', 0)),
      subtotalHighSavingCashDrop: round(_.get(lastYearEndPolicyValue, 'highSavingCashDrop', 0)),
      grandTotalPremium: d(subtotalAnnualRiderPremium)
        .plus(subtotalAnnualBasicPremium)
        .toNumber(),
    }
  }

  const _generatePolicyValues = (
    policyValueParams,
    basicPlan,
    cashValueRates,
    riderPremiumRatesPayload,
    occupationFactorForRider,
    cashDropRates,
    payerOccupationFactorForRider,
    RPURates,
    ETIRates,
    PENRates
  ) => {
    const {
      age,
      gender,
      sumAssured,
      basicPremium,
      modelFactorPeriod,
      validSelectedRiders,
      payerAge,
      payerGender,
      modelFactor,
      basicPlanQuery,
      userAgentType,
    } = policyValueParams
    const initialAge = getInitialAge(age)
    const ageRangeWithinCoveragePeriod = getAgeRangeWithinCoveragePeriod(initialAge, basicPlan.coveragePeriod)

    const cashDropRate = findCashDropRate(basicPlan.code, cashDropRates)
    const RPURate = findRPURate(basicPlan.code, RPURates)
    const ETIRate = findETIRate(basicPlan.code, ETIRates)
    const PENRate = findPENRate(basicPlan.code, PENRates)
    const generatePolicyValueRow = getGenerateYearEndPolicyValue({
      basicPlan,
      cashDropRate,
      basicPlanQuery,
      RPURate,
      ETIRate,
    })

    const yearEndPolicyValues = ageRangeWithinCoveragePeriod.reduce((previousValue, yearEndAge) => {
      const yearEnd = yearEndAge - initialAge

      const annualBasicPremium = calculateAnnualBasicPremium({
        basicPremium,
        modelFactorPeriod,
      })
      const cumulativeBasicPremium = _accumulateBasicPremium(
        age,
        yearEnd,
        basicPlan.paymentPeriod,
        d(annualBasicPremium)
      )

      return [
        ...previousValue,
        generatePolicyValueRow({
          yearEnd,
          riders: validSelectedRiders,
          basicPlan,
          basicPremium,
          cumulativeBasicPremium,
          gender,
          yearEndAge: {
            value: yearEndAge,
            unit: 'year',
          },
          occupationFactorForRider,
          modelFactor,
          cashValueRates,
          cashDropRates,
          cashDropRate,
          basicSumAssured: sumAssured,
          modelFactorPeriod,
          riderPremiumRatesPayload,
          initialInsuredAge: {
            value: initialAge,
            unit: 'year',
          },
          payerAge,
          payerGender,
          previousYearEndValue: previousValue[previousValue.length - 1],
          payerOccupationFactorForRider,
          RPURate,
          ETIRate,
          PENRate,
          userAgentType,
        }),
      ]
    }, [])

    return {
      yearEndPolicyValues,
      ...getGenerateTotalFromYearEndPolicyValues({ basicPlan, yearEndPolicyValues, cashDropRate }),
    }
  }

  return {
    _generatePensionEndYear,
    _generatePension,
    _generatePolicyValuesForRetirement,
    generatePolicyValues,
    _generatePolicyValues,
    _generatePolicyValuesForLifeSavePro,
    _generatePolicyValuesForHealthTopping,
  }
}
